import React, { useState } from 'react';
import { createWorkflowWatch } from '../../utils/api';
import './WatchSetup.css';

const DAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' }
];

const money = value => Number(value || 0).toLocaleString(undefined, {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0
});

/**
 * Turning a finished run into a standing watch.
 *
 * Every field here is one the system genuinely cannot decide: how often, at
 * what hour, how big a change is worth an email. Everything else — which
 * workflow, which version, that it may only propose — is carried from the run
 * that produced this offer and is shown as a statement, not as an option,
 * because they are not the user's to change here.
 *
 * The contract line is deliberately the last thing above the button. Creating
 * a recurring automation is the kind of thing people should read one sentence
 * about before they agree to it.
 */
const WatchSetup = ({
  workflowRef, workflowTitle, hotelId, currentOrgId,
  versionId = null, year = null, versionName = null,
  issuePatternKey = null,
  onCreated, onCancel
}) => {
  const [frequency, setFrequency] = useState('daily');
  const [daysOfWeek, setDaysOfWeek] = useState([1]);
  const [time, setTime] = useState('07:00');
  const [materialityAmount, setMaterialityAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  // An event detector has no cadence to choose: it fires when the pattern it
  // was delegated to appears. Showing a schedule picker for it would ask the
  // user to decide something that does not apply.
  const isDetector = !!issuePatternKey;
  const threshold = Number(materialityAmount) > 0 ? Number(materialityAmount) : 0;
  const when = isDetector
    ? 'whenever this issue pattern appears at this property'
    : frequency === 'weekly'
    ? `${daysOfWeek.map(d => DAYS.find(day => day.value === d)?.label).filter(Boolean).join(', ') || '—'} at ${time}`
    : `every day at ${time}`;

  const toggleDay = (value) => setDaysOfWeek(prev => (
    prev.includes(value) ? prev.filter(day => day !== value) : [...prev, value].sort()
  ));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await createWorkflowWatch({
        workflowRef, hotelId, currentOrgId, versionId, year, versionName,
        issuePatternKey,
        frequency, time,
        daysOfWeek: frequency === 'weekly' ? daysOfWeek : null,
        materialityAmount: threshold || null,
        // The browser knows the user's timezone; the server falls back to the
        // property's when this is absent. Guessing UTC would send the 7am mail
        // in the middle of the night.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null
      });
      setCreated(result);
      if (onCreated) onCreated(result);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="ws-panel is-done">
        <strong>Watch created</strong>
        <div className="ws-contract">{created.contract?.summary}</div>
        {created.watch?.nextScheduled && (
          <div className="ws-note">First run: {new Date(created.watch.nextScheduled).toLocaleString()}</div>
        )}
      </div>
    );
  }

  return (
    <div className="ws-panel">
      <strong>Watch {workflowTitle || workflowRef}</strong>
      <div className="ws-note">
        {versionName ? <>Pinned to <b>{versionName}</b>{year ? ` (${year})` : ''}. </> : null}
        It runs the same review you just read and emails you when something needs approving.
      </div>

      <div className="ws-fields">
        {!isDetector && <label>
          <span>How often</span>
          <select value={frequency} onChange={event => setFrequency(event.target.value)} disabled={busy}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>}
        {!isDetector && <label>
          <span>Time</span>
          <input type="time" value={time} onChange={event => setTime(event.target.value)} disabled={busy} />
        </label>}
        <label>
          <span>Only email me above</span>
          <input
            type="number"
            min="0"
            step="500"
            placeholder="any amount"
            value={materialityAmount}
            onChange={event => setMaterialityAmount(event.target.value)}
            disabled={busy}
          />
        </label>
      </div>

      {!isDetector && frequency === 'weekly' && (
        <div className="ws-days" role="group" aria-label="Days of the week">
          {DAYS.map(day => (
            <button
              key={day.value}
              type="button"
              className={daysOfWeek.includes(day.value) ? 'is-on' : ''}
              onClick={() => toggleDay(day.value)}
              disabled={busy}
            >{day.label}</button>
          ))}
        </div>
      )}

      <div className="ws-contract">
        {isDetector ? 'Notifies you ' : 'Runs '}{when}. Emails you only when there are changes
        {threshold > 0 ? ` worth at least ${money(threshold)}` : ''} to approve.
        {' '}<b>It proposes; it never applies.</b>
      </div>

      {error && <div className="ws-error">{error}</div>}

      <div className="ws-actions">
        <button
          className="ws-primary"
          onClick={submit}
          disabled={busy || (!isDetector && frequency === 'weekly' && daysOfWeek.length === 0)}
        >{busy ? 'Creating…' : 'Create watch'}</button>
        <button onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
};

export default WatchSetup;
